import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/apiClient'
import type {
  OwnerSettings,
  OwnerSettingsResponse,
  OwnerTrialView,
  InvoiceSettings,
  InvoicePrintFormat,
} from '@/types/backend'

type NormalizedOwnerSettings = {
  digestEnabled: boolean
  digestHour: string
  tz: string
  dunningEnabled: boolean
  invoice: InvoiceSettings
}

type UpdateOwnerSettingsPayload = Partial<{
  digestEnabled: boolean
  digestHour: string
  tz: string
  dunningEnabled: boolean
  invoice: Partial<InvoiceSettings>
}>

const DEFAULT_INVOICE: InvoiceSettings = {
  printFormat: 'A4',
  businessName: '',
  businessPhone: '',
  businessAddress: '',
  logoUrl: '',
}

const DEFAULTS: NormalizedOwnerSettings = {
  digestEnabled: true,
  digestHour: '21:30',
  tz: 'Africa/Abidjan',
  dunningEnabled: true,
  invoice: DEFAULT_INVOICE,
}

const isPrintFormat = (value: unknown): value is InvoicePrintFormat =>
  value === 'A4' || value === 'THERMAL_80' || value === 'THERMAL_58'

const normalizeInvoice = (
  invoice?: Partial<InvoiceSettings> | null
): InvoiceSettings => {
  return {
    printFormat: isPrintFormat(invoice?.printFormat)
      ? invoice!.printFormat
      : DEFAULT_INVOICE.printFormat,
    businessName:
      typeof invoice?.businessName === 'string'
        ? invoice.businessName
        : DEFAULT_INVOICE.businessName,
    businessPhone:
      typeof invoice?.businessPhone === 'string'
        ? invoice.businessPhone
        : DEFAULT_INVOICE.businessPhone,
    businessAddress:
      typeof invoice?.businessAddress === 'string'
        ? invoice.businessAddress
        : DEFAULT_INVOICE.businessAddress,
    logoUrl:
      typeof invoice?.logoUrl === 'string'
        ? invoice.logoUrl
        : DEFAULT_INVOICE.logoUrl,
  }
}

const normalize = (s?: OwnerSettings | null): NormalizedOwnerSettings => {
  const tzFromBrowser =
    Intl.DateTimeFormat?.().resolvedOptions?.().timeZone || DEFAULTS.tz

  return {
    digestEnabled: s?.digestEnabled ?? DEFAULTS.digestEnabled,
    digestHour: s?.digestHour ?? DEFAULTS.digestHour,
    tz: s?.tz ?? tzFromBrowser,
    dunningEnabled: s?.dunningEnabled ?? DEFAULTS.dunningEnabled,
    invoice: normalizeInvoice(s?.invoice),
  }
}

export const useOwnerSettingsQuery = (enabled = true) => {
  return useQuery({
    queryKey: ['owner-settings'],
    enabled,
    queryFn: async () => {
      const { data } = await apiClient.get<OwnerSettingsResponse>(
        '/owners/me/settings'
      )
      return normalize(data?.settings)
    },
  })
}

export const useUpdateOwnerSettingsMutation = () => {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: async (payload: UpdateOwnerSettingsPayload) => {
      const { data } = await apiClient.patch<OwnerSettingsResponse>(
        '/owners/me/settings',
        payload
      )
      return normalize(data?.settings)
    },
    onSuccess: (settings) => {
      qc.setQueryData(['owner-settings'], settings)
    },
  })
}

export const useOwnerQuery = (enabled = true) =>
  useQuery({
    queryKey: ['owner'],
    enabled,
    queryFn: async () => {
      const { data } = await apiClient.get<OwnerTrialView>('/owners/me/owner')
      return data
    },
  })

export type { NormalizedOwnerSettings, UpdateOwnerSettingsPayload }
